import type { ShareCardConfig } from '@tabbio/types';

/**
 * The share editor's unsaved draft, kept per CV target so a Free user's
 * tried-out paid options survive the paywall round trip (the editor's modal
 * must fully close before /upgrade can present, which unmounts it). Nothing
 * here is saved to the CV: Save writes through CVDataProvider, and the draft
 * is dropped at that point.
 *
 * The store is module state; on web it is mirrored into sessionStorage so a
 * reload mid-upgrade keeps the draft too. Every storage access is guarded:
 * a private window or a blocked origin must never break the editor.
 */
const drafts = new Map<string, ShareCardConfig>();

const STORAGE_PREFIX = 'tabbio-share-draft:';

function storage(): Storage | null {
  try {
    if (typeof globalThis === 'undefined') return null;
    const candidate = (globalThis as { sessionStorage?: Storage }).sessionStorage;
    return candidate ?? null;
  } catch {
    return null;
  }
}

/** One key per CV: the master CV and every tailored CV keep their own draft. */
export function shareCardDraftKey(target: string | null | undefined): string {
  return target && target !== 'master' ? `cv:${target}` : 'cv:master';
}

export function readShareCardDraft(key: string): ShareCardConfig | null {
  const inMemory = drafts.get(key);
  if (inMemory) return inMemory;
  try {
    const raw = storage()?.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const card = parsed as ShareCardConfig;
    if (typeof card.background !== 'string') return null;
    drafts.set(key, card);
    return card;
  } catch {
    return null;
  }
}

export function writeShareCardDraft(key: string, card: ShareCardConfig): void {
  drafts.set(key, card);
  try {
    storage()?.setItem(STORAGE_PREFIX + key, JSON.stringify(card));
  } catch {
    /* storage unavailable: the in-memory copy still carries the draft */
  }
}

export function clearShareCardDraft(key: string): void {
  drafts.delete(key);
  try {
    storage()?.removeItem(STORAGE_PREFIX + key);
  } catch {
    /* nothing to clear */
  }
}

/** Test seam. */
export function resetShareCardDrafts(): void {
  drafts.clear();
}
